import React, { useState, useEffect } from "react";
import {
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import {
  analyzeTeacherConflicts,
  exportTeacherConflictReport,
  getTeacherConflictStats,
  TEACHER_CONFLICT_TYPE_LABELS,
} from "../utils/teacherConflictAnalysis";

// Row numbers listed in the "not checked" note before it switches to a count
const SKIPPED_ROWS_SHOWN = 20;
// Combined sessions listed in full before the rest are counted
const COMBINED_SESSIONS_SHOWN = 10;

const TeacherConflictAnalysis = ({ excelData, fileName }) => {
  const [conflictAnalysis, setConflictAnalysis] = useState(null);
  const [conflictStats, setConflictStats] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showDetails, setShowDetails] = useState({});
  const [exportMessage, setExportMessage] = useState(""); // Add export message state

  // Results describe the rows they were found in; drop them once those rows change
  // (a room re-allocation, a name fix) so a stale list is never shown as current
  const rows = excelData ? excelData.data : null;
  useEffect(() => {
    setConflictAnalysis(null);
    setConflictStats(null);
    setShowDetails({});
  }, [rows]);

  // Run teacher conflict analysis
  const handleAnalyzeConflicts = async () => {
    if (!excelData) {
      alert("No Excel data available for analysis");
      return;
    }

    setIsAnalyzing(true);
    try {
      console.log("🔍 Starting teacher conflict analysis...");
      const analysis = analyzeTeacherConflicts(excelData);
      const stats = getTeacherConflictStats(analysis);

      setConflictAnalysis(analysis);
      setConflictStats(stats);

      console.log(
        `✅ Teacher conflict analysis completed: ${analysis.totalConflicts} conflict(s), ${analysis.rowsChecked} rows checked, ${analysis.skippedRows.length} skipped`
      );
    } catch (error) {
      console.error("❌ Error analyzing teacher conflicts:", error);
      alert(`Error analyzing conflicts: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Export conflict reports
  const handleExportReports = () => {
    if (!conflictAnalysis || !conflictAnalysis.hasConflicts) {
      setExportMessage("No conflicts to export");
      setTimeout(() => setExportMessage(""), 3000);
      return;
    }

    const result = exportTeacherConflictReport(conflictAnalysis, fileName || "teacher_conflicts");
    if (result.success) {
      setExportMessage(`✅ ${result.message}`);
    } else {
      setExportMessage(`❌ Export failed: ${result.message}`);
    }

    // Clear message after 5 seconds
    setTimeout(() => setExportMessage(""), 5000);
  };

  // Toggle conflict details
  const toggleConflictDetails = (conflictId) => {
    setShowDetails((prev) => ({
      ...prev,
      [conflictId]: !prev[conflictId],
    }));
  };

  return (
    <div className="teacher-conflict-analysis bg-white rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <UserGroupIcon className="h-8 w-8 text-orange-500 mr-3" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Teacher Conflict Analysis</h2>
            <p className="text-sm text-gray-600">
              Find teachers booked twice in one time slot, and rooms given to two teachers at once
            </p>
          </div>
        </div>

        {!conflictAnalysis && (
          <button
            onClick={handleAnalyzeConflicts}
            disabled={!excelData || isAnalyzing}
            className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Analyzing...
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                Find Conflicts
              </>
            )}
          </button>
        )}
      </div>

      {/* What Find Conflicts checks - shown until it has been run */}
      {!conflictAnalysis && excelData && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 space-y-4">
          <div>
            <p className="font-semibold text-gray-900 mb-1">How Find Conflicts works</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>It takes every row that has a tutor (column E).</li>
              <li>
                It reads each row&apos;s time slot from Day/Time and EW/Time (columns F and G): a
                day, Mon to Sun, and a period, AM, PM or Eve. For example &quot;Thu AM&quot;.
              </li>
              <li>It compares only rows that fall in the same time slot.</li>
            </ol>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-red-800 mb-1">Flagged as a conflict</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>
                  <span className="font-medium">Teacher double-booked</span>: the same tutor has
                  two or more sessions in one slot, meaning they are in different rooms or
                  teaching different units. Rows with no room yet are included.
                </li>
                <li>
                  <span className="font-medium">Room shared</span>: two or more different tutors
                  are in the same room (column J) in one slot.
                </li>
              </ul>
            </div>

            <div>
              <p className="font-semibold text-green-800 mb-1">Not a conflict</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>
                  The same tutor, unit (column D) and room in one slot. This is one combined
                  session listed on several rows, and it is shown in a note after the run. Rows
                  need a unit name to be combined.
                </li>
                <li>Different tutors in different rooms at the same time.</li>
                <li>The same tutor in different slots, for example Thu AM and Thu PM.</li>
                <li>Rows with no tutor.</li>
                <li>
                  Rows whose day or period cannot be read. They are listed after the run as not
                  checked.
                </li>
              </ul>
            </div>
          </div>

          <div className="text-gray-600 space-y-1">
            <p>
              <span className="font-medium text-gray-700">Matching:</span> tutor and unit names
              ignore capitals and extra spaces, and nothing else. &quot;Dr A Smith&quot; and
              &quot;A Smith&quot; count as two people, so run Name Resolution first.
            </p>
            <p>
              <span className="font-medium text-gray-700">Brackets:</span> text in brackets is
              ignored, so &quot;Mon (Wk1) AM&quot; and &quot;Mon (Wk2) AM&quot; are the same slot.
            </p>
          </div>
        </div>
      )}

      {/* Messages */}
      {exportMessage && (
        <div
          className={`p-4 rounded-lg mb-6 ${
            exportMessage.includes("❌") || exportMessage.includes("failed")
              ? "bg-red-100 text-red-700 border border-red-300"
              : "bg-green-100 text-green-700 border border-green-300"
          }`}
        >
          {exportMessage}
        </div>
      )}

      {/* Analysis Results */}
      {conflictAnalysis && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-red-600">Total Conflicts</p>
                  <p className="text-2xl font-bold text-red-900">
                    {conflictStats?.totalConflicts || 0}
                  </p>
                  <p className="text-xs text-red-700">
                    {conflictStats?.doubleBookings || 0} double-booked ·{" "}
                    {conflictStats?.sharedRooms || 0} room shared
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center">
                <UserGroupIcon className="h-8 w-8 text-orange-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-orange-600">Teachers Affected</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {conflictStats?.totalTeachersAffected || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-blue-600">Rooms Affected</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {conflictStats?.totalRoomsAffected || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-purple-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-purple-600">Time Slots</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {conflictStats?.totalTimeSlotsAffected || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Rows that could not be checked */}
          {conflictAnalysis.skippedRows.length > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <span className="font-medium">
                {conflictAnalysis.skippedRows.length} row(s) with a tutor were not checked
              </span>{" "}
              because Day/Time and EW/Time do not name a day and AM, PM or Eve: rows{" "}
              {conflictAnalysis.skippedRows.slice(0, SKIPPED_ROWS_SHOWN).join(", ")}
              {conflictAnalysis.skippedRows.length > SKIPPED_ROWS_SHOWN &&
                ` and ${conflictAnalysis.skippedRows.length - SKIPPED_ROWS_SHOWN} more`}
              .
            </div>
          )}

          {/* Rows not flagged because they are one session listed more than once */}
          {conflictAnalysis.combinedSessions.length > 0 && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <p>
                <span className="font-medium">
                  {conflictAnalysis.combinedSessions.length} combined session(s) not flagged
                </span>
                : the same tutor, unit and room in one slot, listed on more than one row. If one of
                these rows should be a different period, correct it in the file.
              </p>
              <ul className="mt-2 ml-5 list-disc space-y-1">
                {conflictAnalysis.combinedSessions
                  .slice(0, COMBINED_SESSIONS_SHOWN)
                  .map((session) => (
                    <li key={session.rows.join("-")}>
                      Rows {session.rows.join(", ")}: {session.teacher}, {session.unitName},{" "}
                      {session.timeSlotLabel}
                      {session.room ? `, room ${session.room}` : ", no room yet"}
                    </li>
                  ))}
              </ul>
              {conflictAnalysis.combinedSessions.length > COMBINED_SESSIONS_SHOWN && (
                <p className="mt-1">
                  and {conflictAnalysis.combinedSessions.length - COMBINED_SESSIONS_SHOWN} more
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {conflictAnalysis.hasConflicts && (
            <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg">
              <div>
                <h3 className="font-semibold text-gray-900">Analysis Complete</h3>
                <p className="text-sm text-gray-600">{conflictAnalysis.summary}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleExportReports}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                >
                  <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                  Export Reports
                </button>
                <button
                  onClick={handleAnalyzeConflicts}
                  className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200"
                >
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                  Re-analyze
                </button>
              </div>
            </div>
          )}

          {/* No Conflicts Message */}
          {!conflictAnalysis.hasConflicts && (
            <div className="text-center py-8 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-green-900 mb-2">
                No Teacher Conflicts Found!
              </h3>
              <p className="text-green-700 mb-4">
                Checked {conflictAnalysis.rowsChecked} rows: no tutor is in two sessions in the
                same slot, and no room has two different tutors in the same slot.
              </p>
              <button
                onClick={handleAnalyzeConflicts}
                className="text-green-600 hover:text-green-800 font-medium"
              >
                Run Analysis Again
              </button>
            </div>
          )}

          {/* Conflicts List */}
          {conflictAnalysis.hasConflicts && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-2" />
                Detected Conflicts ({conflictAnalysis.totalConflicts})
              </h3>

              {conflictAnalysis.conflicts.map((conflict, index) => (
                <div key={conflict.id} className="border border-red-200 rounded-lg bg-red-50">
                  {/* Conflict Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-red-100 transition-colors duration-200"
                    onClick={() => toggleConflictDetails(conflict.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full mr-3">
                          #{index + 1}
                        </span>
                        <div>
                          <h4 className="font-semibold text-red-900">
                            {conflict.type === "teacher_double_booked"
                              ? conflict.conflictingTeachers[0]
                              : `Room ${conflict.rooms[0]}`}
                            <span className="ml-2 align-middle text-xs font-medium bg-red-200 text-red-800 px-2 py-0.5 rounded-full">
                              {TEACHER_CONFLICT_TYPE_LABELS[conflict.type]}
                            </span>
                          </h4>
                          <p className="text-sm text-red-700">{conflict.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-red-800">
                          {conflict.type === "teacher_double_booked"
                            ? `${conflict.conflictingAllocations.length} Sessions`
                            : `${conflict.conflictingTeachers.length} Teachers`}
                        </p>
                        <p className="text-xs text-red-600">
                          Excel Rows: {conflict.affectedRows.join(", ")}
                        </p>
                        {conflict.affectedItems && conflict.affectedItems.length > 0 && (
                          <p className="text-xs text-red-500">
                            {conflict.affectedItems.map((item, idx) => (
                              <span key={idx} className="inline-block mr-1">
                                Item no {item}
                              </span>
                            ))}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Conflict Details */}
                  {showDetails[conflict.id] && (
                    <div className="border-t border-red-200 p-4 bg-white">
                      {/* Conflicting Allocations */}
                      <h5 className="font-semibold text-gray-900 mb-3">Conflicting Allocations</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {conflict.conflictingAllocations.map((allocation, idx) => (
                          <div key={idx} className="bg-gray-50 p-4 rounded border">
                            <div className="flex justify-between items-start gap-4">
                              <div>
                                <p className="text-lg font-medium text-gray-900">
                                  {allocation.teacher}
                                </p>
                                <p className="text-base text-gray-700">{allocation.unitName}</p>
                                <p className="text-sm text-gray-600">
                                  {allocation.originalDayTime} {allocation.originalEwTime} ·{" "}
                                  {allocation.room ? `Room ${allocation.room}` : "No room"}
                                </p>
                              </div>
                              <div className="text-right whitespace-nowrap">
                                <p className="text-base font-medium text-blue-600">
                                  {allocation.studentsCount} students
                                </p>
                                <p className="text-sm text-gray-600">Row {allocation.rowNumber}</p>
                                {allocation.itemNo && (
                                  <p className="text-sm text-gray-500">
                                    Item no {allocation.itemNo}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No Data Message */}
      {!excelData && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <XCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Excel Data Available</h3>
          <p className="text-gray-600">
            Please upload an Excel file first to analyze teacher conflicts.
          </p>
        </div>
      )}
    </div>
  );
};

export default TeacherConflictAnalysis;
